import { handleSubscriptionPurchase } from '@lib/purchase';
import {
  isWalled,
  PlanAccess,
  planAccessFromBilling,
  resolvePlanAccess,
} from '@lib/utils/planAccess';
import { SECOND } from '@lib/utils/time';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { billingStore } from './Billing';
import { debugStore } from './Debug';
import { CheckoutSource, trackingStore } from './Tracking';
import { userStore } from './User';

/**
 * The paywall: whether the app is open to the current user, and the checkout that opens it.
 *
 * The verdict (`access`) is latched once per page load — the first time billing settles after
 * the session is verified — and again only when this store changes it: after a checkout is
 * confirmed, or on an explicit `recheck()` from the wall. It deliberately does not follow
 * `billingStore.data` afterwards: a subscription ending mid-session must not drop the wall
 * over work in progress, and the backend already refuses every paid action for such an
 * account. A reload re-evaluates.
 *
 * Checkout from the wall runs in the same tab. Stripe brings the user back to whatever
 * `STRIPE_PAYMENT_SUCCESS_PATH` the backend is deployed with, so the return is recognised by
 * a sessionStorage record written before leaving, not by the landing route: wherever the user
 * lands, the wall sees the record and polls `/billing/info` until the account is entitled.
 */

const PENDING_CHECKOUT_KEY = 'paywall.pendingCheckout';

// `/billing/info` is live (the backend caches only `/user/me`'s copy of the flags) and rate
// limited to 30 requests a minute; 3s keeps a confirmation well under that. Stripe's webhook
// normally lands within seconds of the redirect; 90s is generous, and past it the user is
// told activation is still pending rather than left on a spinner.
const CONFIRM_POLL_INTERVAL_MS = 3 * SECOND;
const CONFIRM_TIMEOUT_MS = 90 * SECOND;

/**
 * Routes a walled user may still open: the wall, the account and billing settings behind it
 * (invoices, portal, password), the checkout return pages, and the debug tab so the wall can
 * be forced on and off from there.
 */
const REACHABLE_WHILE_WALLED = [
  '/subscribe',
  '/settings/account',
  '/settings/billing',
  '/settings/debug',
  '/payment/',
] as const;

export const isReachableWhileWalled = (pathname: string): boolean =>
  REACHABLE_WHILE_WALLED.some((prefix) =>
    prefix.endsWith('/') ? pathname.startsWith(prefix) : pathname === prefix,
  );

/**
 * What `checkout()` wrote before leaving for Stripe. Both fields are null when the record
 * was created by the return page instead (a checkout that started in another tab, so this
 * one never saw it begin).
 */
type PendingCheckout = { productId: string | null; source: CheckoutSource | null };

const CHECKOUT_SOURCES: readonly CheckoutSource[] = ['paywall', 'pricing-page'];

const readPendingCheckout = (): PendingCheckout | null => {
  const raw = sessionStorage.getItem(PENDING_CHECKOUT_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || !('productId' in parsed)) return null;
    const { productId } = parsed;
    const source = 'source' in parsed ? parsed.source : null;
    return {
      productId: typeof productId === 'string' ? productId : null,
      source: CHECKOUT_SOURCES.find((known) => known === source) ?? null,
    };
  } catch {
    return null;
  }
};

const writePendingCheckout = (pending: PendingCheckout) =>
  sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(pending));

const clearPendingCheckout = () => sessionStorage.removeItem(PENDING_CHECKOUT_KEY);

export type CheckoutConfirmation = 'idle' | 'confirming' | 'timed-out';

export class PaywallStore {
  private crossStoreReactionsBound = false;

  @observable
  private latched: PlanAccess | null = null;

  /** Progress of the post-checkout wait for `/billing/info` to report the account entitled. */
  @observable
  confirmation: CheckoutConfirmation = 'idle';

  /**
   * Debug-tab override: force one of the wall's variants regardless of billing. Only read in
   * debug mode. `'entitled'` is not offered — a walled account is walled.
   */
  @observable
  debugForcedAccess: Extract<PlanAccess, 'no-plan' | 'lapsed'> | null = null;

  // Bumped to abandon an in-flight confirmation (cancel, logout, a newer run).
  private confirmRun = 0;

  constructor() {
    makeObservable(this);
  }

  @computed
  get access(): PlanAccess | null {
    if (this.debugForcedAccess && debugStore.respectfulDebugMode) return this.debugForcedAccess;
    return this.latched;
  }

  @computed
  get isWalled(): boolean {
    return isWalled(this.access);
  }

  /**
   * Billing settled without a verdict: the fetch failed and `/user/me` cannot vouch for a
   * subscription. The caller shows a retry — neither the app nor the wall is a safe guess.
   */
  @computed
  get isUndecidable(): boolean {
    return this.access === null && !!billingStore.error && !billingStore.data;
  }

  /** Bound from `initCrossStoreReactions()`; see that module for why not the constructor. */
  initCrossStoreReactions() {
    if (this.crossStoreReactionsBound) return;
    this.crossStoreReactionsBound = true;

    // Latch the first verdict billing can give once the session is verified. Unverified
    // users have no billing document yet (the fetch 404s) and are sent to /verify-email
    // before any of this matters.
    reaction(
      () => ({
        billing: billingStore.data,
        error: billingStore.error,
        user: userStore.data,
      }),
      ({ billing, error, user }) => {
        if (this.latched !== null) return;
        if (!user?.isVerified) return;
        if (!billing && !error) return;
        const access = resolvePlanAccess(billing, user);
        if (access !== null) this.latch(access);
      },
      { fireImmediately: true },
    );

    reaction(
      () => userStore.isLoggedIn,
      (isLoggedIn) => {
        if (!isLoggedIn) this.reset();
      },
    );
  }

  @action
  private latch(access: PlanAccess) {
    this.latched = access;
    const pending = readPendingCheckout();
    if (this.isWalled) {
      if (pending) void this.confirmPendingCheckout();
    } else if (pending) {
      // The common case: Stripe's webhook beat the redirect, so the first billing read after
      // checkout already says entitled and there is nothing to poll for.
      clearPendingCheckout();
      trackingStore.subscriptionActivated({ productId: pending.productId, source: pending.source });
    }
  }

  @action
  private reset() {
    this.latched = null;
    this.confirmation = 'idle';
    this.confirmRun++;
    clearPendingCheckout();
  }

  /**
   * Start Stripe checkout for `productId` in this tab. The pending record is written first so
   * the return is recognised, and removed again if checkout never opened.
   */
  checkout = async (productId: string, source: CheckoutSource): Promise<void> => {
    trackingStore.checkoutStarted({ productId, productType: 'subscription', source });
    writePendingCheckout({ productId, source });
    try {
      await handleSubscriptionPurchase(productId, 'same-tab');
    } catch (error) {
      clearPendingCheckout();
      throw error;
    }
  };

  /**
   * The Stripe success page landed in a walled tab. Make sure a pending record exists (a
   * checkout started from the Pricing page in another tab has none) and start confirming.
   * An entitled tab has nothing to wait for.
   */
  markCheckoutReturned = () => {
    if (!this.isWalled) return;
    if (!readPendingCheckout()) writePendingCheckout({ productId: null, source: null });
    if (this.confirmation !== 'confirming') void this.confirmPendingCheckout();
  };

  /** The Stripe cancel page landed: nothing to wait for. */
  cancelPendingCheckout = action(() => {
    clearPendingCheckout();
    this.confirmRun++;
    this.confirmation = 'idle';
  });

  /** Re-read billing and re-latch: after the Stripe portal, or when the user asks. */
  recheck = async (): Promise<void> => {
    await billingStore.forceRefreshBillingData();
    const billing = billingStore.data;
    if (billing) this.latch(planAccessFromBilling(billing));
  };

  private async confirmPendingCheckout(): Promise<void> {
    const pending = readPendingCheckout();
    if (!pending) return;
    const run = ++this.confirmRun;
    runInAction(() => {
      this.confirmation = 'confirming';
    });

    const deadline = Date.now() + CONFIRM_TIMEOUT_MS;
    while (Date.now() < deadline) {
      try {
        await billingStore.forceRefreshBillingData();
      } catch (error) {
        // A failed poll is retried on the next tick; the deadline bounds the wait.
        console.warn('Paywall: billing refresh failed while confirming checkout', error);
      }
      if (this.confirmRun !== run) return;

      const billing = billingStore.data;
      if (billing && !billing.isFree) {
        clearPendingCheckout();
        trackingStore.subscriptionActivated({
          productId: pending.productId,
          source: pending.source,
        });
        runInAction(() => {
          this.confirmation = 'idle';
          this.latched = planAccessFromBilling(billing);
        });
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, CONFIRM_POLL_INTERVAL_MS));
      if (this.confirmRun !== run) return;
    }

    clearPendingCheckout();
    runInAction(() => {
      this.confirmation = 'timed-out';
    });
  }
}

export const paywallStore = new PaywallStore();
